/**
 * Melbourne timezone helpers.
 *
 * The workshop operates in Australia/Melbourne. We standardise on this tz for
 * ALL date display and date INPUT so behaviour is deterministic regardless of
 * the browser's local timezone (e.g. a SA travelling, or a server running in
 * UTC).
 */

const MEL_TZ = 'Australia/Melbourne';

/**
 * Build a JS Date object whose absolute time equals the given Melbourne-local
 * wall-clock (year, month, day, hour, minute). Handles DST automatically.
 *
 * Example: melbourneLocalDate(2026, 3, 20, 9, 0) === the Date for
 * "2026-03-20 09:00 Melbourne", which is 2026-03-19T22:00:00Z (in AEDT) or
 * 2026-03-19T23:00:00Z (in AEST).
 */
export function melbourneLocalDate(year, month, day, hour = 9, minute = 0) {
  // Treat the wanted local time as if it were UTC; this is our first guess.
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  // Ask Intl what time Melbourne thinks utcGuess corresponds to.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: MEL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(utcGuess).map((p) => [p.type, p.value])
  );
  const melTime = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour, 10),
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  const wanted = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offsetMs = wanted - melTime;
  return new Date(utcGuess.getTime() + offsetMs);
}

/**
 * Compose an ISO 8601 (UTC, ends with 'Z') string for a Melbourne wall-clock
 * date. Pass either ("YYYY-MM-DD", "HH:mm") or (year, month, day, hour, min).
 */
export function melbourneISO(dateStr, timeStr = '09:00') {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  const [hh = 9, mm = 0] = (timeStr || '09:00').split(':').map((n) => parseInt(n, 10));
  return melbourneLocalDate(y, m, d, hh || 9, mm || 0).toISOString();
}

/**
 * Format an ISO string (or Date) into Melbourne local time using en-AU locale.
 * Defaults to "20 Mar 2026, 09:00 am" style output.
 */
export function formatMelbourne(value, options = {}) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    timeZone: MEL_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/** Format date-only (no time) in Melbourne. */
export function formatMelbourneDate(value, options = {}) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-AU', {
    timeZone: MEL_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options,
  });
}

/** Format time-only in Melbourne. */
export function formatMelbourneTime(value, options = {}) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-AU', {
    timeZone: MEL_TZ,
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/** Return the "YYYY-MM-DD" Melbourne calendar date for an ISO/Date. */
export function melbourneDateKey(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: MEL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(date); // en-CA emits YYYY-MM-DD
}

/** Return the "HH:mm" Melbourne wall-clock time for an ISO/Date. */
export function melbourneTimeKey(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: MEL_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return fmt.format(date); // returns "HH:mm"
}


/**
 * Format a JS Date as a "YYYY-MM-DD" string using its **Melbourne**
 * calendar day (not UTC, not browser-local). Replaces the common
 * `date.toISOString().split('T')[0]` anti-pattern which silently drops a
 * day for users east of UTC (e.g. Melbourne Mon 15 June 00:30 AEST
 * becomes Sun 14 June 14:30 UTC → toISOString gives "2026-06-14").
 *
 * Use this whenever you need a calendar-day string that should reflect
 * the workshop's local date — dashboard week-start, daily filters,
 * booking schedule keys, etc.
 */
export function toMelbourneISODate(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  // en-CA gives ISO-style "YYYY-MM-DD" output directly.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MEL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}


/**
 * Format a `delivery_due_at` value for display on the JC tile / wallet
 * chip / modal header.  iter138.9v — the input is a *naïve* local string
 * `YYYY-MM-DDTHH:mm` (no timezone), captured from <input type="datetime-local">
 * on the SM's browser. We intentionally do NOT round-trip via `new Date()`
 * because that would shift by the browser's TZ-vs-Melbourne offset.
 *
 * Output: "Fri 20 Feb · 2:00 pm"
 *
 * Falls back to en-AU locale formatting if the string contains a TZ
 * marker (legacy data from before we switched away from `.toISOString()`).
 */
export function formatDeliveryDueLabel(raw) {
  if (!raw) return '';
  const naive = String(raw).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/
  );
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (naive) {
    const [, year, mon, day, hr, min] = naive;
    // Get weekday by constructing a UTC midnight and asking en-AU.
    const utc = new Date(`${year}-${mon}-${day}T00:00:00Z`);
    const weekday = utc.toLocaleDateString('en-AU', {
      weekday: 'short', timeZone: 'UTC',
    });
    let h = parseInt(hr, 10);
    const ampm = h < 12 ? 'am' : 'pm';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${weekday} ${day} ${MONTHS[parseInt(mon, 10) - 1]} · ${h}:${min} ${ampm}`;
  }
  // Legacy ISO-with-TZ branch — render in Melbourne TZ.
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString('en-AU', {
      timeZone: MEL_TZ, weekday: 'short', day: '2-digit', month: 'short',
    });
    const timePart = d.toLocaleTimeString('en-AU', {
      timeZone: MEL_TZ, hour: 'numeric', minute: '2-digit', hour12: true,
    }).toLowerCase();
    return `${datePart} · ${timePart}`;
  } catch (_e) { return ''; }
}
